<template>
  <div class="admin-panel">

    <admin-header
      variant="full"
      :crumbs="[{ label: 'Client Management' }]"
      :session-countdown="sessionCountdown"
      :formatted-countdown="formattedCountdown"
      @open-logout="openLogoutModal"
    />

    <!-- ── MAIN ──────────────────────────────────────────────────── -->
    <transition name="ap-page">
    <main v-if="pageReady" class="ap-container">

      <!-- Page heading -->
      <div class="ap-page-head">
        <div>
          <h1>Client Management</h1>
          <div class="ap-sub">Onboard new clients, manage existing accounts, and monitor activity.</div>
        </div>
      </div>

      <!-- Stat cards -->
      <admin-stat-cards
        class="d-none"
        :total-clients="accountList.length"
        :joined-this-month="joinedThisMonth"
        :joined-this-week="joinedThisWeek"
        :filtered-count="filteredCount"
      />

      <!-- Task widget -->
      <admin-task-widget :total-clients="accountList.length" />

      <!-- Client table -->
      <admin-client-table
        :account-list="accountList"
        @view="openViewModal"
        @remove="openRemoveConfirm"
        @filtered-count-change="filteredCount = $event"
      />

    </main>
    </transition>

    <global-loader />

    <!-- ── MODALS ─────────────────────────────────────────────────── -->

    <alert-modal :show="showPopup" :message="popupMessage" :status="popupStatus" @close="closePopup" />

    <session-modal :show="showAdminSessionModal" @continue="continueAdminSession" @signout="signOutAdminSession">
      Your admin session has expired. Continue to refresh your session or sign out.
    </session-modal>

    <confirm-modal
      :show="showLogoutModal"
      title="Confirm Logout"
      confirm-label="Logout"
      :icon="['fas', 'sign-out-alt']"
      @cancel="closeLogoutModal"
      @confirm="confirmLogout"
    >Are you sure you want to logout?</confirm-modal>

    <client-details-modal
      :show="showViewModal"
      :client="viewClient"
      :documents="clientDocuments"
      :docs-loading="docsLoading"
      @close="showViewModal = false"
      @download-doc="downloadAdminDocument"
      @delete-doc="deleteDocument"
    />

    <confirm-modal
      :show="showRemoveConfirmModal"
      title="Remove Client"
      confirm-label="Remove"
      :icon="['fas', 'user-minus']"
      @cancel="showRemoveConfirmModal = false"
      @confirm="confirmRemoveClient"
    >
      <template v-if="removeConfirmClient">
        Remove <strong>{{ removeConfirmClient.client }}</strong>?<br>This action cannot be undone.
      </template>
    </confirm-modal>

  </div>
</template>

<script>
import adminSessionMixin from '@/mixins/adminSessionMixin'
import AdminHeader from '@/components/admin/AdminHeader.vue'
import AdminStatCards from '@/components/admin/AdminStatCards.vue'
import AdminTaskWidget from '@/components/admin/AdminTaskWidget.vue'
import AdminClientTable from '@/components/admin/AdminClientTable.vue'
import ClientDetailsModal from '@/components/shared/ClientDetailsModal.vue'
import ConfirmModal from '@/components/shared/ConfirmModal.vue'
import SessionModal from '@/components/shared/SessionModal.vue'
import AlertModal from '@/components/shared/AlertModal.vue'
import GlobalLoader from '@/components/shared/GlobalLoader.vue'
import { showLoader, hideLoader } from '@/services/loader'

export default {
  name: 'admin-panel',
  mixins: [adminSessionMixin],
  components: {
    AdminHeader,
    AdminStatCards,
    AdminTaskWidget,
    AdminClientTable,
    ClientDetailsModal,
    ConfirmModal,
    SessionModal,
    AlertModal,
    GlobalLoader
  },
  metaInfo () {
    const title = 'Tyng & Co | Admin Panel'
    const description = 'Admin Panel'
    document.querySelector('meta[name="description"]').content = description
    return {
      title,
      titleTemplate: null,
      meta: [{ vmid: 'description', name: 'description', content: description }],
      link: [{ rel: 'canonical', href: `https://tyng-ca.com.my/${this.$i18n.locale}/admin-panel` }]
    }
  },
  data () {
    return {
      pageReady: false,
      accountList: [],
      filteredCount: 0,
      // Modals
      showViewModal: false,
      viewClient: null,
      clientDocuments: [],
      docsLoading: false,
      showRemoveConfirmModal: false,
      removeConfirmClient: null,
      // Popup
      showPopup: false,
      popupMessage: '',
      popupActionType: '',
      popupStatus: 'success',
      // Remove (table-row path)
      removeUsername: ''
    }
  },
  computed: {
    joinedThisMonth () {
      const now = new Date()
      return this.accountList.filter(c => {
        if (!c.created_at) return false
        const d = new Date(c.created_at)
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
      }).length
    },
    joinedThisWeek () {
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
      return this.accountList.filter(c => c.created_at && new Date(c.created_at) >= weekAgo).length
    }
  },
  watch: {
    showPopup () { this.toggleBodyScroll() },
    showLogoutModal () { this.toggleBodyScroll() },
    showAdminSessionModal () { this.toggleBodyScroll() },
    showViewModal () { this.toggleBodyScroll() },
    showRemoveConfirmModal () { this.toggleBodyScroll() }
  },
  mounted () {
    this.fetchAccountList()
    this.startSessionTimer()
  },
  methods: {
    toggleBodyScroll () {
      const open = this.showPopup || this.showLogoutModal || this.showAdminSessionModal ||
        this.showViewModal || this.showRemoveConfirmModal
      document.body.style.overflow = open ? 'hidden' : ''
    },

    async fetchAccountList () {
      showLoader()
      try {
        const data = await this.adminFetch('/api/admin/clients/list.php')
        if (data === null) { this.pendingAdminRetry = () => this.fetchAccountList(); return }
        this.accountList = data.data || []
      } catch (err) {
        console.error('Failed to fetch account list:', err)
      } finally {
        hideLoader()
        this.pageReady = true
      }
    },

    openViewModal (client) {
      this.viewClient = client
      this.clientDocuments = []
      this.showViewModal = true
      this.loadClientDocuments(client.id)
    },
    async loadClientDocuments (clientId) {
      this.docsLoading = true
      try {
        const data = await this.adminFetch(`/api/admin/clients/documents/list.php?client_id=${clientId}`)
        if (data) this.clientDocuments = data.data || []
      } catch (err) {
        console.error('Failed to load client documents:', err)
      } finally {
        this.docsLoading = false
      }
    },
    async deleteDocument (docId) {
      try {
        const data = await this.adminFetch('/api/admin/clients/documents/delete.php', {
          method: 'POST',
          body: JSON.stringify({ id: docId })
        })
        if (data === null) return
        this.clientDocuments = this.clientDocuments.filter(d => d.id !== docId)
      } catch (err) {
        console.error('Failed to delete document:', err)
      }
    },
    async downloadAdminDocument (doc) {
      try {
        const token = localStorage.getItem('adminToken')
        const res = await fetch(`https://tyng-ca.com.my/api/admin/clients/documents/download.php?id=${doc.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!res.ok) return
        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url; a.download = doc.original_name; a.click()
        URL.revokeObjectURL(url)
      } catch (err) { console.error('Download failed:', err) }
    },

    openRemoveConfirm (client) {
      this.removeConfirmClient = client
      this.showRemoveConfirmModal = true
    },
    confirmRemoveClient () {
      this.removeUsername = this.removeConfirmClient.username
      this.showRemoveConfirmModal = false
      this.removeClientById()
    },
    async removeClientById () {
      try {
        const data = await this.adminFetch('/api/admin/clients/delete.php', {
          method: 'POST',
          body: JSON.stringify({ username: this.removeUsername })
        })
        if (data === null) { this.pendingAdminRetry = () => this.removeClientById(); return }
        this.openPopup(data.message, 'remove', 'success')
        // Remove locally — no need to re-fetch list.php
        this.accountList = this.accountList.filter(c => c.username !== this.removeUsername)
      } catch (err) {
        this.openPopup(err?.message || 'Failed to remove client.', 'remove', 'error')
      }
    },

    openPopup (message, actionType = '', status = 'success') {
      this.popupMessage = message || 'Action completed.'
      this.popupActionType = actionType
      this.popupStatus = status
      this.showPopup = true
    },
    closePopup () {
      this.showPopup = false
      this.popupMessage = ''
      this.popupActionType = ''
      this.popupStatus = 'success'
      document.body.style.overflow = ''
    }
  }
}
</script>

<style scoped>
.ap-page-enter-active { transition: opacity 0.4s ease, transform 0.4s cubic-bezier(0.22, 1, 0.36, 1); }
.ap-page-enter { opacity: 0; transform: translateY(16px); }
</style>
